import axios from 'axios';
import { getActingDoctor, SECRETARY_GATEWAY_PREFIX, setActingDoctor } from './gateway';
import { pickActingDoctor, type SecretaryDoctor } from './pickActing';

export type { SecretaryDoctor } from './pickActing';
export { pickActingDoctor } from './pickActing';

/**
 * Always on: whoever a doctor invited by number is a secretary the moment they log in with it, no flag decides that.
 * Nothing remembers «not a secretary»: a doctor can invite a number at any moment, and that person must see the panel on the very next login.
 */
export const isSecretaryGatewayEnabled = () => true;

/** Ask the gateway and make the acting doctor effective at once (before anything that needs it can fire a request). */
export const fetchSecretarySession = async (): Promise<{ doctors: SecretaryDoctor[] }> => {
  try {
    const { doctors } = (await axios.get<{ doctors: SecretaryDoctor[] }>(`${SECRETARY_GATEWAY_PREFIX}/session`, { withCredentials: true })).data;
    const acting = pickActingDoctor(doctors, getActingDoctor());
    setActingDoctor(acting?.id ?? null, acting?.scopes);
    return { doctors };
  } catch (error) {
    setActingDoctor(null); // not logged in or the gateway is down: behave as a plain user
    throw error;
  }
};

/**
 * A hint for the very first render of a later page load: «this user is acting for that doctor». It only decides what the first
 * frame looks like (no flash of the doctor's own home); the gateway still checks every call and the session is asked again at
 * boot, which corrects or clears it. Kept per user so another login on the same device never inherits it.
 */
const HINT_KEY = 'p24:secretary-hint';
const writeHint = (userId: string, doctor: SecretaryDoctor | null) => {
  try {
    if (doctor) window.localStorage.setItem(HINT_KEY, JSON.stringify({ userId, doctorId: doctor.id, scopes: doctor.scopes }));
    else window.localStorage.removeItem(HINT_KEY);
  } catch {
    /* storage blocked: the first frame may flash, nothing else */
  }
};

/** Call as early as the user id is known (before first paint); a no-op unless the hint belongs to this very user. */
export const restoreSecretaryHint = (userId: string) => {
  if (typeof window === 'undefined' || !isSecretaryGatewayEnabled()) return;
  try {
    const hint = JSON.parse(window.localStorage.getItem(HINT_KEY) ?? 'null');
    if (hint && String(hint.userId) === String(userId) && hint.doctorId) setActingDoctor(String(hint.doctorId), Array.isArray(hint.scopes) ? hint.scopes : []);
  } catch {
    /* unreadable hint: ignore */
  }
};

let inflight: { userId: string; promise: Promise<SecretaryDoctor | null> } | null = null;

/**
 * App boot, once per user: whose secretary is this login? Resolves before the app syncs «my centers», so a secretary's home is
 * built from the doctor's centers. null for everybody else (doctors, patients) and whenever the feature is off.
 */
export const resolveSecretaryContext = (userId: string): Promise<SecretaryDoctor | null> => {
  if (typeof window === 'undefined' || !isSecretaryGatewayEnabled()) return Promise.resolve(null);
  if (inflight?.userId === userId) return inflight.promise;

  const promise = (async () => {
    try {
      const { doctors } = await fetchSecretarySession();
      const acting = pickActingDoctor(doctors, getActingDoctor());
      writeHint(userId, acting);
      return acting;
    } catch {
      return null;
    }
  })();
  inflight = { userId, promise };
  return promise;
};

/** drop everything remembered about acting for a doctor (logout, account switch) */
export const clearSecretaryContext = (userId?: string) => {
  setActingDoctor(null);
  inflight = null;
  try {
    window.localStorage.removeItem(HINT_KEY);
  } catch {
    /* storage blocked */
  }
};
